import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Card,
  Statistic,
  Tabs,
  Tooltip,
  Popconfirm,
  Typography,
  Select,
  DatePicker
} from 'antd';
import { PlusOutlined, EyeOutlined, CheckCircleOutlined, DeleteOutlined, FileExcelOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import apiClient from '../services/api';
import { DisposalRequest, DisposedAsset, DisposalStats } from '../types/disposal';
import { useAuth } from '../hooks/useAuth';
import AssetDetailModal from '../components/AssetDetailModal';
import AssetCreateForm from './AssetCreateForm';

const { Search } = Input;
const { Option } = Select;
const { Title } = Typography;
const { TabPane } = Tabs;

// Custom hooks
const useDisposalData = () => {
  const [disposalRequests, setDisposalRequests] = useState<DisposalRequest[]>([]);
  const [disposedAssets, setDisposedAssets] = useState<DisposedAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<DisposalStats | null>(null);

  const loadStats = useCallback(async () => {
    try {
      const response = await apiClient.getDisposalStats() as any;
      setStats(response);
    } catch (error: any) {
      message.error('Lỗi khi tải thống kê');
    }
  }, []);

  const loadDisposalRequests = useCallback(async (params: any) => {
    setLoading(true);
    try {
      const response = await apiClient.getDisposalRequests(params) as any;
      setDisposalRequests(response.data);
      // ✅ CẬP NHẬT: Trả về total để cập nhật pagination
      return response.pagination?.total || 0;
    } catch (error: any) {
      let errorMessage = 'Lỗi khi tải danh sách yêu cầu thanh lý';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      const formattedMessage = errorMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      message.error({
        content: <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />,
        duration: 5
      });
      return 0;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDisposedAssets = useCallback(async (params: any) => {
    setLoading(true);
    try {
      const response = await apiClient.getDisposedAssets(params) as any;
      setDisposedAssets(response.data);
      // ✅ CẬP NHẬT: Trả về total để cập nhật pagination
      return response.pagination?.total || 0;
    } catch (error: any) {
      message.error('Lỗi khi tải danh sách tài sản đã thanh lý');
      return 0;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    disposalRequests,
    disposedAssets,
    loading,
    stats,
    loadStats,
    loadDisposalRequests,
    loadDisposedAssets
  };
};

const useDisposalFilters = () => {
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    company_id: undefined as number | undefined,
    employee_id: undefined as number | undefined,
    location_id: undefined as number | undefined,
    date_from: '',
    date_to: '',
  });

  const handleFilterChange = useCallback((key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      status: '',
      search: '',
      company_id: undefined,
      employee_id: undefined,
      location_id: undefined,
      date_from: '',
      date_to: '',
    });
  }, []);

  return {
    filters,
    setFilters,
    handleFilterChange,
    resetFilters
  };
};

const useDisposalPagination = () => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const handleTableChange = useCallback((newPagination: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  }, []);

  const resetPagination = useCallback(() => {
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
  }, []);

  return {
    pagination,
    setPagination,
    handleTableChange,
    resetPagination
  };
};

const DisposalPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('disposal-requests');
  const { disposalRequests, disposedAssets, loading, stats, loadStats, loadDisposalRequests, loadDisposedAssets } = useDisposalData();
  const { filters, setFilters, handleFilterChange, resetFilters } = useDisposalFilters();
  const { pagination, setPagination, handleTableChange, resetPagination } = useDisposalPagination();
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<DisposalRequest | null>(null);
  const [approveForm] = Form.useForm();
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [assetModalVisible, setAssetModalVisible] = useState(false);
  const [recreateModalVisible, setRecreateModalVisible] = useState(false);
  const [recreateSourceAsset, setRecreateSourceAsset] = useState<DisposedAsset | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [assetsForForm, setAssetsForForm] = useState<any[]>([]);

  useEffect(() => {
    loadStats();
    loadFilterData(); // ✅ THÊM: Load dữ liệu filter
    // ❌ XÓA: Không gọi API ngay lập tức vì filters chưa sẵn sàng
    // loadDisposalRequests({ page: 1, limit: 10, ...filters });
    // loadDisposedAssets({ page: 1, limit: 10, ...filters });
  }, []); // filters, loadStats, loadDisposalRequests, loadDisposedAssets will be stable after first render

  // ✅ THÊM MỚI: Cập nhật pagination.total khi load dữ liệu
  const handleLoadData = useCallback(async () => {
    if (activeTab === 'disposal-requests') {
      const total = await loadDisposalRequests({ page: pagination.current, limit: pagination.pageSize, ...filters });
      setPagination(prev => ({ ...prev, total }));
    } else {
      const total = await loadDisposedAssets({ page: pagination.current, limit: pagination.pageSize, ...filters });
      setPagination(prev => ({ ...prev, total }));
    }
  }, [activeTab, pagination.current, pagination.pageSize, filters, loadDisposalRequests, loadDisposedAssets]);

  // ✅ CẬP NHẬT: Sử dụng handleLoadData thay vì gọi trực tiếp
  useEffect(() => {
    handleLoadData();
  }, [activeTab, pagination.current, pagination.pageSize, filters]);

  // ✅ THÊM MỚI: Load dữ liệu ban đầu sau khi filter data đã sẵn sàng
  useEffect(() => {
    if (companies.length > 0 && employees.length > 0 && locations.length > 0) {
      // Filter data đã sẵn sàng, load dữ liệu ban đầu
      handleLoadData();
    }
  }, [companies, employees, locations, handleLoadData]);

  // Reset filters khi chuyển tab
  useEffect(() => {
    resetFilters();
    resetPagination();
  }, [activeTab, resetFilters, resetPagination]); // setFilters dependency will be stable after first render

  // Tự động set company_id cho company_admin và manager
  useEffect(() => {
    if (user && (user.role === 'company_admin' || user.role === 'manager') && user.company_id) {
      setFilters(prev => ({
        ...prev,
        company_id: user.company_id
      }));
    }
  }, [user]);

  // ✅ THÊM MỚI: Filter nhân viên và vị trí theo công ty đã chọn
  const filteredEmployees = useMemo(() => {
    if (!filters.company_id) return employees;
    return employees.filter(emp => emp.company_id === filters.company_id);
  }, [employees, filters.company_id]);

  const filteredLocations = useMemo(() => {
    if (!filters.company_id) return locations;
    return locations.filter(loc => loc.company_id === filters.company_id);
  }, [locations, filters.company_id]);

  // ✅ THÊM MỚI: Reset employee_id và location_id khi company_id thay đổi
  useEffect(() => {
    if (filters.company_id) {
      // Kiểm tra xem employee_id hiện tại có thuộc company mới không
      const currentEmployee = employees.find(emp => emp.id === filters.employee_id);
      if (!currentEmployee || currentEmployee.company_id !== filters.company_id) {
        handleFilterChange('employee_id', undefined);
      }

      // Kiểm tra xem location_id hiện tại có thuộc company mới không
      const currentLocation = locations.find(loc => loc.id === filters.location_id);
      if (!currentLocation || currentLocation.company_id !== filters.company_id) {
        handleFilterChange('location_id', undefined);
      }
    } else {
      // Nếu không có company_id, reset employee_id và location_id
      handleFilterChange('employee_id', undefined);
      handleFilterChange('location_id', undefined);
    }
  }, [filters.company_id, employees, locations, filters.employee_id, filters.location_id, handleFilterChange]);

  const loadFilterData = async () => {
    try {
      const [companiesRes, employeesRes, locationsRes] = await Promise.all([
        apiClient.getCompanies() as any,
        apiClient.getEmployees() as any,
        apiClient.getLocations() as any,
      ]);
      setCompanies(Array.isArray(companiesRes) ? companiesRes : (companiesRes.data || []));
      setEmployees(Array.isArray(employeesRes) ? employeesRes : (employeesRes.data || []));
      setLocations(Array.isArray(locationsRes) ? locationsRes : (locationsRes.data || []));
    } catch (error) {
      message.error('Lỗi khi tải dữ liệu filter');
    }
  };

  // Nạp dữ liệu cơ bản cho form tạo mới khi mở modal
  useEffect(() => {
    if (recreateModalVisible) {
      (async () => {
        try {
          const [categoriesRes, suppliersRes] = await Promise.all([
            apiClient.getAssetCategories() as any,
            apiClient.getSuppliers() as any,
          ]);
          setCategories(Array.isArray(categoriesRes) ? categoriesRes : (categoriesRes.data || []));
          setSuppliers(Array.isArray(suppliersRes) ? suppliersRes : (suppliersRes.data || []));
          setAssetsForForm([]);
        } catch { }
      })();
    }
  }, [recreateModalVisible]);

  const handleSearch = (value: string) => {
    handleFilterChange('search', value);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteDisposalRequest(id);
      message.success('Xóa yêu cầu thanh lý thành công');
      // ✅ CẬP NHẬT: Cập nhật pagination.total
      const total = await loadDisposalRequests({ page: pagination.current, limit: pagination.pageSize, ...filters });
      setPagination(prev => ({ ...prev, total }));
    } catch (error: any) {
      message.error('Lỗi khi xóa yêu cầu thanh lý');
    }
  };

  const handleApprove = async (values: { approval_document_code: string }) => {
    if (!selectedRequest) return;

    try {
      await apiClient.approveDisposalRequest(selectedRequest.id, {
        approval_document_code: values.approval_document_code
      });
      message.success('Duyệt yêu cầu thanh lý thành công');
      setApproveModalVisible(false);
      setSelectedRequest(null);
      approveForm.resetFields();
      // ✅ CẬP NHẬT: Cập nhật pagination.total
      const total = await loadDisposalRequests({ page: pagination.current, limit: pagination.pageSize, ...filters });
      setPagination(prev => ({ ...prev, total }));
    } catch (error: any) {
      let errorMessage = 'Lỗi khi duyệt yêu cầu thanh lý';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      const formattedMessage = errorMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      message.error({
        content: <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />,
        duration: 5
      });
    }
  };

  const showApproveModal = (request: DisposalRequest) => {
    setSelectedRequest(request);
    setApproveModalVisible(true);
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'pending': 'orange',
      'approved': 'green',
      'rejected': 'red',
    };
    return colorMap[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const textMap: { [key: string]: string } = {
      'pending': 'Chờ duyệt',
      'approved': 'Đã duyệt (Hoàn thành)',
      'rejected': 'Từ chối',
    };
    return textMap[status] || status;
  };

  const disposalRequestColumns = [
    {
      title: 'Mã yêu cầu',
      dataIndex: 'request_code',
      key: 'request_code',
      align: 'left' as const,
      render: (code: string) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{code}</span>
      ),
    },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      key: 'title',
      align: 'left' as const,
      ellipsis: true,
    },
    {
      title: 'Người tạo',
      dataIndex: 'requested_by_name',
      key: 'requested_by_name',
      align: 'center' as const,
    },
    ...(user?.role !== 'super_admin' ? [{
      title: 'Công ty',
      dataIndex: 'company_name',
      key: 'company_name',
      align: 'center' as const,
    }] : []),
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'center' as const,
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Số lượng TS',
      dataIndex: 'asset_count',
      key: 'asset_count',
      align: 'center' as const,
      render: (count: number) => <Tag color="blue">{count || 0}</Tag>,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 150,
      render: (_: any, record: DisposalRequest) => (
        <Space>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/disposal-requests/${record.id}`)}
              size="small"
            />
          </Tooltip>
          {record.status === 'pending' && (
            <>
              <Tooltip title="Duyệt yêu cầu">
                <Button
                  type="text"
                  icon={<CheckCircleOutlined />}
                  onClick={() => showApproveModal(record)}
                  size="small"
                />
              </Tooltip>
              <Popconfirm
                title="Bạn có chắc chắn muốn xóa yêu cầu thanh lý này?"
                onConfirm={() => handleDelete(record.id)}
                okText="Có"
                cancelText="Không"
              >
                <Tooltip title="Xóa">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  const disposedAssetColumns = [
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      align: 'left' as const,
      render: (code: string) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{code}</span>
      ),
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'name',
      key: 'name',
      align: 'left' as const,
      ellipsis: true,
    },
    {
      title: 'Công ty',
      dataIndex: 'company_name',
      key: 'company_name',
      align: 'center' as const,
    },
    {
      title: 'Nhân viên sử dụng',
      dataIndex: 'employee_name',
      key: 'employee_name',
      align: 'center' as const,
      width: 180,
    },
    {
      title: 'Ngày thanh lý',
      dataIndex: 'disposal_date',
      key: 'disposal_date',
      align: 'center' as const,
      render: (date: 'string') => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Yêu cầu thanh lý',
      dataIndex: 'disposal_request_code',
      key: 'disposal_request_code',
      align: 'center' as const,
      render: (code: string) => (
        <Tag color="blue">{code}</Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      render: (_: any, record: DisposedAsset) => (
        <Space>
          <Tooltip title="Xem chi tiết">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedAssetId(record.id);
                setAssetModalVisible(true);
              }}
              size="small"
            />
          </Tooltip>
          {user?.role !== 'employee_user' && (
            <Tooltip title="Tạo tài sản mới từ bản này">
              <Button
                type="text"
                icon={<CopyOutlined />}
                size="small"
                onClick={() => {
                  setRecreateSourceAsset(record);
                  setRecreateModalVisible(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'inline-block' }}>
          Thanh lý Tài sản
        </Title>
        {user?.role !== 'employee_user' && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/disposal/create')}
            style={{ float: 'right' }}
            size="large"
          >
            Tạo Yêu cầu Thanh lý
          </Button>
        )}
      </div>

      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Tổng yêu cầu thanh lý"
                value={stats.total}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Chờ duyệt"
                value={stats.pending}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Đã duyệt"
                value={stats.approved}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Từ chối"
                value={stats.rejected}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === 'disposal-requests' && (
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col span={5}>
              <DatePicker
                placeholder="Từ ngày"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                onChange={(date) => handleFilterChange('date_from', date ? date.format('YYYY-MM-DD') : '')}
              />
            </Col>
            <Col span={5}>
              <DatePicker
                placeholder="Đến ngày"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                onChange={(date) => handleFilterChange('date_to', date ? date.format('YYYY-MM-DD') : '')}
              />
            </Col>
            <Col span={5}>
              <Select
                placeholder="Trạng thái"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => handleFilterChange('status', value)}
                value={filters.status}
              >
                <Option value="pending">Chờ duyệt</Option>
                <Option value="approved">Đã duyệt</Option>
                <Option value="rejected">Từ chối</Option>
              </Select>
            </Col>
            <Col span={5}>
              <Select
                placeholder="Người tạo"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => handleFilterChange('requested_by', value)}
                value={filters.employee_id}
              >
                {employees.map(employee => (
                  <Select.Option key={employee.id} value={employee.id}>{employee.full_name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Button icon={<FileExcelOutlined />} onClick={() => message.info('Tính năng xuất Excel đang được phát triển')}>
                Xuất Excel
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {activeTab === 'disposed-assets' && (
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col span={6}>
              <Search
                placeholder="Tìm kiếm theo mã, tên tài sản..."
                onSearch={handleSearch}
                allowClear
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Công ty"
                allowClear={user?.role === 'super_admin'}
                style={{ width: '100%' }}
                value={filters.company_id}
                onChange={(value) => handleFilterChange('company_id', value)}
                disabled={user?.role !== 'super_admin'}
              >
                {companies.map(company => (
                  <Select.Option key={company.id} value={company.id}>{company.name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Nhân viên"
                allowClear
                style={{ width: '100%' }}
                value={filters.employee_id}
                onChange={(value) => handleFilterChange('employee_id', value)}
                disabled={!filters.company_id}
                notFoundContent={
                  !filters.company_id
                    ? "Vui lòng chọn công ty trước"
                    : filteredEmployees.length === 0
                      ? "Không có nhân viên nào cho công ty này"
                      : "Không tìm thấy"
                }
              >
                {filteredEmployees.map(employee => (
                  <Select.Option key={employee.id} value={employee.id}>{employee.full_name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Vị trí"
                allowClear
                style={{ width: '100%' }}
                value={filters.location_id}
                onChange={(value) => handleFilterChange('location_id', value)}
                disabled={!filters.company_id}
                notFoundContent={
                  !filters.company_id
                    ? "Vui lòng chọn công ty trước"
                    : filteredLocations.length === 0
                      ? "Không có vị trí nào cho công ty này"
                      : "Không tìm thấy"
                }
              >
                {filteredLocations.map(location => (
                  <Select.Option key={location.id} value={location.id}>{location.name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Button icon={<FileExcelOutlined />} onClick={() => message.info('Tính năng xuất Excel đang được phát triển')}>
                Xuất Excel
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          size="large"
        >
          <TabPane tab="Yêu cầu Thanh lý" key="disposal-requests">
            <Table
              columns={disposalRequestColumns}
              dataSource={disposalRequests}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} yêu cầu thanh lý`,
              }}
              onChange={handleTableChange}
            />
          </TabPane>

          <TabPane tab="Tài sản Đã Thanh lý" key="disposed-assets">
            <Table
              columns={disposedAssetColumns}
              dataSource={disposedAssets}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} tài sản đã thanh lý`,
              }}
              onChange={handleTableChange}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Modal tạo tài sản mới từ tài sản đã thanh lý - dùng AssetCreateForm chuẩn */}
      <Modal
        open={recreateModalVisible}
        onCancel={() => { setRecreateModalVisible(false); setRecreateSourceAsset(null); }}
        footer={null}
        title={recreateSourceAsset ? `Tạo mới từ bản đã thanh lý: ${recreateSourceAsset.asset_code}` : 'Tạo mới tài sản'}
        destroyOnClose
        width="min(80vw, 1000px)"
      >
        <AssetCreateForm
          companyList={companies}
          categoryList={categories}
          locationList={locations}
          assetList={assetsForForm}
          supplierList={suppliers}
          employeeList={employees}
          onSuccess={() => {
            setRecreateModalVisible(false);
            setRecreateSourceAsset(null);
            // Sau khi tạo, reload danh sách
            if (activeTab === 'disposed-assets') {
              // ✅ CẬP NHẬT: Cập nhật pagination.total
              loadDisposedAssets({ page: 1, limit: pagination.pageSize, ...filters }).then(total => {
                setPagination(prev => ({ ...prev, total }));
              });
            }
          }}
          onCancel={() => { setRecreateModalVisible(false); setRecreateSourceAsset(null); }}
          onSupplierCreated={() => { }}
          onLocationCreated={() => { }}
          onCategoryCreated={() => { }}
          onEmployeeCreated={() => { }}
          {...(recreateSourceAsset ? {
            initialValues: {
              name: recreateSourceAsset.name,
              description: recreateSourceAsset.description,
              technical_condition: recreateSourceAsset.status === 'disposed' ? 'good' : undefined,
              category_id: recreateSourceAsset.category_id,
              company_id: user?.role === 'super_admin' ? undefined : user?.company_id,
              asset_code: '',
            }
          } : {})}
        />
      </Modal>

      {/* Modal Duyệt Yêu cầu Thanh lý */}
      <Modal
        title="Duyệt Yêu cầu Thanh lý"
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
          setSelectedRequest(null);
          approveForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={approveForm}
          onFinish={handleApprove}
          layout="vertical"
        >
          <Form.Item
            name="approval_document_code"
            label="Hồ sơ bàn sao từ văn phòng điện tử"
            rules={[{ required: true, message: 'Vui lòng nhập hồ sơ bàn sao' }]}
          >
            <Input placeholder="Nhập mã hồ sơ bàn sao..." />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Duyệt
              </Button>
              <Button onClick={() => {
                setApproveModalVisible(false);
                setSelectedRequest(null);
                approveForm.resetFields();
              }}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      <AssetDetailModal
        assetId={selectedAssetId}
        visible={assetModalVisible}
        onClose={() => setAssetModalVisible(false)}
      />
    </div>
  );
};

export default DisposalPage; 